"use client";

import { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { AssetsDirectory, SignupInputFieldName, TranslationScopes } from "@finbackoffice/enums";
import classNames from "classnames";
import {
    ChatServiceContext,
    useLayoutProfileConfig,
    ChatContext,
    useSearchParamsQuery,
    formatSearchParamsToString,
} from "@finbackoffice/site-core";
import { ModalsContextV2, CrmContext, UserAccountContextV2 } from "contexts";
import { useSignupConfigV2 } from "hooks";
import { ILayoutsProfileSection, ProfileLayoutSectionEnum } from "@finbackoffice/fe-core";
import { Svg } from "components/base/svg/Svg";
import Translate from "components/base/translate/Translate";
import Button from "components/base/button/Button";
import AutoHeight from "components/base/auto-height/AutoHeight";
import { usePathname, useRouter } from "next/navigation";
import BetsSection from "../sections/BetsSection";
import MessagesSection from "../sections/MessagesSection";
import MainSection from "../sections/MainSection";
import PromotionsSection from "../sections/PromotionsSection";
import styles from "./account-compact.module.sass";

type IProps = {
    close?: () => Promise<void>;
};

const AccountCompact: FC<IProps> = ({ close }) => {
    const router = useRouter();
    const { query } = useSearchParamsQuery();
    const pathname = usePathname();
    const { directory: directoryParam } = query;

    const { unreadMessages, chatStats } = useContext(ChatContext);
    const { userId, userEmail, username, firstName, lastName, logout } =
        useContext(UserAccountContextV2);
    const { openWalletModal } = useContext(ModalsContextV2);
    const { unreadMessagesCount, openGamificationUi } = useContext(CrmContext);
    const { showWidget, visible } = useContext(ChatServiceContext);

    const { signupConfigObj } = useSignupConfigV2([SignupInputFieldName.Username]);
    const { config: betsLayoutConfig } = useLayoutProfileConfig(ProfileLayoutSectionEnum.Bets);
    const { config: messagesLayoutConfig } = useLayoutProfileConfig(
        ProfileLayoutSectionEnum.Messages,
    );
    const { config: promotionsLayoutConfig } = useLayoutProfileConfig(
        ProfileLayoutSectionEnum.Promotions,
    );
    const { config: mainLayoutConfig } = useLayoutProfileConfig(ProfileLayoutSectionEnum.Main);
    const { getFilteredLayoutProfileConfigs } = useLayoutProfileConfig();
    const allLayoutProfileConfigs = getFilteredLayoutProfileConfigs();

    const [directory, setDirectory] = useState<ProfileLayoutSectionEnum>(
        (directoryParam as ProfileLayoutSectionEnum) || ProfileLayoutSectionEnum.Main,
    );

    const onSignoutClickHandler = useCallback(async () => {
        await close?.();
        await logout();
    }, [logout, close]);

    useEffect(() => {
        if (query) {
            const { directory, subdirectory, ...rest } = query;
            if ((!directory || !subdirectory) && (directory || subdirectory)) {
                router.push(`${pathname}?${formatSearchParamsToString(rest)}`);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (directoryParam) {
            setDirectory(directoryParam as ProfileLayoutSectionEnum);
        }
    }, [directoryParam]);

    const onChatServiceClick = useCallback(() => {
        showWidget();
    }, [showWidget]);

    const renderNavItem = useCallback(
        (section: ILayoutsProfileSection) => {
            let content;
            switch (section.name) {
                case ProfileLayoutSectionEnum.Main:
                    content = (
                        <Button
                            type="button"
                            key={section.name}
                            onClick={() => setDirectory(ProfileLayoutSectionEnum.Main)}
                            data-testid="account-compact-menu-main"
                            variant="custom"
                            className={classNames(
                                styles.menuItem,
                                directory === ProfileLayoutSectionEnum.Main && styles.selected,
                            )}>
                            <div>
                                <Svg
                                    src={`/${AssetsDirectory.Mobile}/${AssetsDirectory.ProfileCompact}/profile-main.svg`}
                                    className="svg-icon"
                                    prefixAppName
                                />
                                <p>
                                    <Translate
                                        tid="account_menu_main_heading"
                                        namespace={TranslationScopes.UserAccount}
                                    />
                                </p>
                            </div>
                        </Button>
                    );
                    break;
                case ProfileLayoutSectionEnum.Bets:
                    content = (
                        <Button
                            type="button"
                            key={section.name}
                            onClick={() => setDirectory(ProfileLayoutSectionEnum.Bets)}
                            data-testid="account-compact-menu-bets"
                            variant="custom"
                            className={classNames(
                                styles.menuItem,
                                directory === ProfileLayoutSectionEnum.Bets && styles.selected,
                            )}>
                            <div>
                                <Svg
                                    src={`/${AssetsDirectory.Mobile}/${AssetsDirectory.ProfileCompact}/profile-my-bets.svg`}
                                    className="svg-icon"
                                    testId="account-compact-menu-bets"
                                    prefixAppName
                                />
                                <p>
                                    <Translate
                                        tid="account_menu_bets_heading"
                                        namespace={TranslationScopes.UserAccount}
                                    />
                                </p>
                            </div>
                        </Button>
                    );
                    break;
                case ProfileLayoutSectionEnum.Agent:
                    content = (
                        <Button
                            type="button"
                            key={section.name}
                            variant="custom"
                            onClick={() => setDirectory(ProfileLayoutSectionEnum.Agent)}
                            className={classNames(
                                styles.menuItem,
                                directory === ProfileLayoutSectionEnum.Agent && styles.selected,
                            )}>
                            <div>
                                <Svg
                                    src={`/${AssetsDirectory.Mobile}/${AssetsDirectory.ProfileCompact}/profile-agent.svg`}
                                    className="svg-icon"
                                    testId="account-compact-menu-agent"
                                    prefixAppName
                                />
                                <p>
                                    <Translate
                                        tid="account_agent_agent"
                                        namespace={TranslationScopes.UserAccount}
                                    />
                                </p>
                            </div>
                        </Button>
                    );
                    break;
                case ProfileLayoutSectionEnum.Promotions:
                    content = (
                        <Button
                            type="button"
                            key={section.name}
                            variant="custom"
                            onClick={() => setDirectory(ProfileLayoutSectionEnum.Promotions)}
                            className={classNames(
                                styles.menuItem,
                                directory === ProfileLayoutSectionEnum.Promotions &&
                                    styles.selected,
                            )}>
                            <div>
                                <Svg
                                    src={`/${AssetsDirectory.Mobile}/${AssetsDirectory.ProfileCompact}/profile-promotions.svg`}
                                    className="svg-icon"
                                    testId="account-compact-menu-promotions"
                                    prefixAppName
                                />
                                <p>
                                    <Translate tid="pageName_bonuses_promotions" />
                                </p>
                            </div>
                        </Button>
                    );
                    break;
                case ProfileLayoutSectionEnum.Messages: {
                    const unreadMessagesMemo =
                        unreadMessages.length +
                        (chatStats?.unread_message ?? 0) +
                        unreadMessagesCount;
                    content = (
                        <Button
                            type="button"
                            key={section.name}
                            variant="custom"
                            onClick={() => setDirectory(ProfileLayoutSectionEnum.Messages)}
                            data-testid="account-compact-menu-messages"
                            className={classNames(
                                styles.menuItem,
                                directory === ProfileLayoutSectionEnum.Messages && styles.selected,
                            )}>
                            <div>
                                <span className={classNames("svg-icon", styles.messagesBtn)}>
                                    <Svg
                                        src={`/${AssetsDirectory.Mobile}/${AssetsDirectory.ProfileCompact}/profile-messages.svg`}
                                        className="svg-icon"
                                        prefixAppName
                                    />
                                    {Boolean(unreadMessagesMemo) && (
                                        <span className={styles.unreadMsgsCount}>
                                            {unreadMessagesMemo}
                                        </span>
                                    )}
                                </span>
                                <p>
                                    <Translate
                                        tid="account_menu_messages_heading"
                                        namespace={TranslationScopes.UserAccount}
                                    />
                                </p>
                            </div>
                        </Button>
                    );
                    break;
                }
                case ProfileLayoutSectionEnum.ChatService:
                    content = (
                        <Button
                            key={section.name}
                            type="button"
                            onClick={onChatServiceClick}
                            variant="custom"
                            disabled={visible}
                            className={styles.menuItem}>
                            <div>
                                <Svg
                                    src={`/${AssetsDirectory.Mobile}/${AssetsDirectory.ProfileCompact}/profile-livechat.svg`}
                                    className="svg-icon"
                                    testId="account-compact-menu-chat"
                                    prefixAppName
                                />
                                <p>
                                    <Translate
                                        tid="account_menu_chat_service_heading"
                                        namespace={TranslationScopes.UserAccount}
                                    />
                                </p>
                            </div>
                        </Button>
                    );

                    break;
                case ProfileLayoutSectionEnum.Wallet:
                    content = (
                        <Button
                            key={section.name}
                            type="button"
                            variant="custom"
                            onClick={() => {
                                openWalletModal();
                            }}
                            className={styles.menuItem}
                            data-testid="account-compact-menu-wallet">
                            <div>
                                <Svg
                                    src={`/${AssetsDirectory.Mobile}/${AssetsDirectory.ProfileCompact}/profile-wallet.svg`}
                                    className="svg-icon"
                                    testId="account-compact-menu-wallet"
                                    prefixAppName
                                />
                                <p>
                                    <Translate tid="header_wallet" />
                                </p>
                            </div>
                        </Button>
                    );
                    break;
                case ProfileLayoutSectionEnum.Gamification:
                    content = (
                        <Button
                            key={section.name}
                            type="button"
                            variant="custom"
                            onClick={() => {
                                openGamificationUi();
                            }}
                            className={styles.menuItem}
                            data-testid="account-compact-menu-gamification">
                            <div>
                                <Svg
                                    src={`/${AssetsDirectory.Mobile}/${AssetsDirectory.ProfileCompact}/gamification.svg`}
                                    className="svg-icon"
                                    prefixAppName
                                />
                                <p>
                                    <Translate
                                        tid="account_menu_gamification_heading"
                                        namespace={TranslationScopes.UserAccount}
                                    />
                                </p>
                            </div>
                        </Button>
                    );
                    break;
                default:
                    return null;
            }
            return content || null;
        },
        [
            directory,
            unreadMessages.length,
            chatStats?.unread_message,
            unreadMessagesCount,
            onChatServiceClick,
            visible,
            openWalletModal,
            openGamificationUi,
        ],
    );

    const renderContent = useMemo(() => {
        switch (directory) {
            case ProfileLayoutSectionEnum.Bets:
                return !!betsLayoutConfig && <BetsSection />;
            case ProfileLayoutSectionEnum.Messages:
                return !!messagesLayoutConfig && <MessagesSection />;
            case ProfileLayoutSectionEnum.Promotions:
                return !!promotionsLayoutConfig && <PromotionsSection />;
            default:
                return !!mainLayoutConfig && <MainSection />;
        }
    }, [
        betsLayoutConfig,
        mainLayoutConfig,
        messagesLayoutConfig,
        promotionsLayoutConfig,
        directory,
    ]);

    return (
        <>
            <div className={styles.header}>
                <div className={styles.userInfo}>
                    <Svg
                        src={`/${AssetsDirectory.Mobile}/${AssetsDirectory.ProfileCompact}/user.svg`}
                        className="svg-icon"
                        prefixAppName
                    />
                    <div>
                        <span>
                            {firstName} {lastName}
                        </span>
                        <span className={styles.userId}>
                            {signupConfigObj.username ? username : userEmail} /{" "}
                            <Translate tid="account_id" namespace={TranslationScopes.UserAccount} />
                            :{userId}
                        </span>
                    </div>
                </div>
                <Button
                    type="button"
                    onClick={onSignoutClickHandler}
                    className={styles.signOutBtn}
                    data-testid="account-exit">
                    <Translate tid="account_exit" namespace={TranslationScopes.UserAccount} />
                    <Svg
                        src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/signout.svg`}
                        className="svg-icon"
                    />
                </Button>
            </div>
            <div className={styles.content} data-testid="account-container">
                <AutoHeight duration={300}>{renderContent}</AutoHeight>
            </div>

            <nav className={styles.menu}>{allLayoutProfileConfigs.map(renderNavItem)}</nav>
        </>
    );
};

export default AccountCompact;
